import { Button, Group, Modal, NumberInput, Select, Stack, Text, TextInput } from "@mantine/core";
import { useState } from "react";
import { ConfirmActionModal } from "@how/ui";
import {
  EMPTY_ROOM,
  useSaveRoom,
  useSetRoomActive,
  validateRoom,
  type ManagedRoom,
  type RoomFields,
} from "./room-queries";

export interface RoomModalProps {
  /** null to add a room, a row to edit it; undefined when closed. */
  readonly room: ManagedRoom | null | undefined;
  readonly otherNames: readonly string[];
  /** Active campuses; the campus box shows only when the church has more than one. */
  readonly campuses: readonly { value: string; label: string }[];
  readonly onClose: () => void;
}

/** One dialog for adding and editing a room, remounted per room by the page's key. */
export function RoomModal({ room, otherNames, campuses, onClose }: RoomModalProps) {
  const save = useSaveRoom();
  const setActive = useSetRoomActive();
  const [fields, setFields] = useState<RoomFields>(
    room === null || room === undefined
      ? EMPTY_ROOM
      : {
          name: room.name,
          campusId: room.campusId,
          ageMin: room.ageMin ?? "",
          ageMax: room.ageMax ?? "",
          capacity: room.capacity ?? "",
          sortOrder: room.sortOrder,
        },
  );
  const [touched, setTouched] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const problems = validateRoom(fields, otherNames);
  const invalid = Object.keys(problems).length > 0;
  const shown = touched ? problems : {};

  const code = (save.error as { code?: string } | null)?.code;
  const errorText =
    code === "23505"
      ? "Another room already has that name."
      : code === "23514"
        ? "Check the ages and the capacity: ages run from 0 to 120 and the oldest cannot be younger than the youngest."
        : code === "42501"
          ? "You need permission to manage events to change rooms."
          : save.isError
            ? "The room was not saved. Try again."
            : null;

  const setNumber = (key: "ageMin" | "ageMax" | "capacity") => (value: number | string) => {
    setFields((current) => ({ ...current, [key]: typeof value === "number" ? value : "" }));
  };

  return (
    <>
      <Modal
        opened={room !== undefined}
        onClose={onClose}
        title={room === null ? "Add a room" : "Edit room"}
        radius="lg"
      >
        <Stack gap="sm">
          <TextInput
            label="Name"
            required
            error={shown.name}
            value={fields.name}
            onChange={(event) => {
              const name = event.currentTarget.value;
              setFields((current) => ({ ...current, name }));
            }}
          />
          {campuses.length > 1 ? (
            <Select
              label="Campus"
              description="Leave empty for a room any campus can use."
              clearable
              data={[...campuses]}
              value={fields.campusId}
              onChange={(campusId) => {
                setFields((current) => ({ ...current, campusId }));
              }}
            />
          ) : null}
          <Group grow align="flex-start">
            <NumberInput
              label="Youngest age"
              description="In years. Empty: no lower limit."
              min={0}
              max={120}
              allowDecimal={false}
              error={shown.ageMin}
              value={fields.ageMin}
              onChange={setNumber("ageMin")}
            />
            <NumberInput
              label="Oldest age"
              description="In years. Empty: no upper limit."
              min={0}
              max={120}
              allowDecimal={false}
              error={shown.ageMax}
              value={fields.ageMax}
              onChange={setNumber("ageMax")}
            />
          </Group>
          <Group grow align="flex-start">
            <NumberInput
              label="Capacity"
              description="Children the room holds. Empty: no limit."
              min={1}
              allowDecimal={false}
              error={shown.capacity}
              value={fields.capacity}
              onChange={setNumber("capacity")}
            />
            <NumberInput
              label="Order"
              description="Lower comes first at the station."
              min={0}
              max={999}
              allowDecimal={false}
              value={fields.sortOrder}
              onChange={(value) => {
                setFields((current) => ({
                  ...current,
                  sortOrder: typeof value === "number" ? value : 0,
                }));
              }}
            />
          </Group>
          <Text size="xs" c="dimmed">
            The station suggests this room for a child whose age falls in the range. A person at the
            door can still choose another room, and capacity is a guide, not a lock.
          </Text>
          {errorText !== null ? (
            <Text size="sm" c="red" role="alert">
              {errorText}
            </Text>
          ) : null}
          <Group justify="space-between" mt="xs">
            <Group gap="xs">
              {room !== null && room !== undefined ? (
                room.isActive ? (
                  <Button
                    variant="subtle"
                    color="red"
                    onClick={() => {
                      setConfirming(true);
                    }}
                  >
                    Archive
                  </Button>
                ) : (
                  <Button
                    variant="subtle"
                    loading={setActive.isPending}
                    onClick={() => {
                      setActive.mutate({ roomId: room.id, isActive: true }, { onSuccess: onClose });
                    }}
                  >
                    Restore
                  </Button>
                )
              ) : null}
            </Group>
            <Button
              loading={save.isPending}
              onClick={() => {
                setTouched(true);
                if (invalid) return;
                save.mutate({ roomId: room?.id ?? null, fields }, { onSuccess: onClose });
              }}
            >
              {room === null ? "Add room" : "Save room"}
            </Button>
          </Group>
        </Stack>
      </Modal>

      <ConfirmActionModal
        opened={confirming}
        onClose={() => {
          setConfirming(false);
        }}
        title="Archive room"
        message={
          room === null || room === undefined
            ? ""
            : `Archive ${room.name}? The station stops offering it. Children already checked in keep their room on the roster and labels, and you can restore it later.`
        }
        confirmLabel="Archive room"
        danger
        loading={setActive.isPending}
        onConfirm={() => {
          if (room === null || room === undefined) return;
          setActive.mutate(
            { roomId: room.id, isActive: false },
            {
              onSuccess: () => {
                setConfirming(false);
                onClose();
              },
            },
          );
        }}
      />
    </>
  );
}
